import { partRunPlaceId } from "../../rail/PartRunPhase/segments";
import type { BookTimelineLayout } from "@/lib/timeline/book/BookLayout";
import { locationBranchKey, locationRunRole } from "../LocationRun/branchKey";
import { partRunPhase } from "../../rail/PartRunPhase/phase";
import type { LocationRun } from "../LocationRun/runs";
import { allLocationRuns } from "../LocationRun/catalog";
import type { ActiveLocationMember } from "./index";

const runsByLayout = new WeakMap<BookTimelineLayout, Map<string, LocationRun[]>>();

/** A loaded book's runs never change, so they are sorted onto their rails once. */
function runsByBranch(layout: BookTimelineLayout): Map<string, LocationRun[]> {
  let byBranch = runsByLayout.get(layout);
  if (byBranch === undefined) {
    byBranch = new Map();
    for (const run of allLocationRuns(layout)) {
      const key = locationBranchKey(run, layout);
      const list = byBranch.get(key);
      if (list === undefined) {
        byBranch.set(key, [run]);
      } else {
        list.push(run);
      }
    }
    runsByLayout.set(layout, byBranch);
  }
  return byBranch;
}

export function branchIds(layout: BookTimelineLayout): string[] {
  return [...runsByBranch(layout).keys()].sort((a, b) => a.localeCompare(b));
}

export function runsOnBranch(
  layout: BookTimelineLayout,
  branchId: string,
): LocationRun[] {
  return runsByBranch(layout).get(branchId) ?? [];
}

export function siteRunOnBranch(
  layout: BookTimelineLayout,
  branchId: string,
  playheadMs?: number,
): LocationRun | undefined {
  const primaries = runsOnBranch(layout, branchId).filter(
    (run) => locationRunRole(run) === "primary",
  );
  if (primaries.length === 0) {
    return undefined;
  }
  const branch = layout.rail.branches.find((item) => item.id === branchId);
  if (playheadMs === undefined) {
    if (branch !== undefined && (branch.lane ?? 0) !== 0) {
      return primaries.find((run) => run.runId === branchId) ?? primaries[0];
    }
    return primaries[0];
  }
  const onScene = primaries.find(
    (run) => playheadMs >= run.startMs && playheadMs < run.endMs,
  );
  if (onScene !== undefined) {
    return onScene;
  }
  if (branch !== undefined && (branch.lane ?? 0) !== 0) {
    const railSite = primaries.find((run) => run.runId === branchId);
    if (railSite !== undefined && playheadMs < railSite.endMs) {
      return railSite;
    }
  }
  let best: LocationRun | undefined;
  for (const run of primaries) {
    if (run.startMs > playheadMs) {
      continue;
    }
    if (best === undefined || run.endMs > best.endMs) {
      best = run;
    }
  }
  return best ?? primaries[0];
}

export function partPlaceId(run: LocationRun): string {
  return partRunPlaceId(run.runId);
}

function memberLabel(
  runs: readonly LocationRun[],
  runId: string,
): string {
  return runs.find((run) => run.runId === runId)?.locationLabel ?? runId;
}

export function groupLabel(
  runs: readonly LocationRun[],
  members: readonly ActiveLocationMember[],
): string {
  return members
    .filter((member) => member.visible)
    .map((member) => memberLabel(runs, member.runId))
    .join(" · ");
}

export function primaryMember(runId: string): ActiveLocationMember {
  return {
    runId,
    role: "primary",
    phase: "site",
    visible: true,
  };
}

export function secondaryMember(
  layout: BookTimelineLayout,
  run: LocationRun,
  playheadMs: number,
): ActiveLocationMember {
  return {
    runId: run.runId,
    role: "secondary",
    phase: partRunPhase(layout.rail, run, playheadMs).kind,
    visible: true,
  };
}
